import dayjs from 'dayjs';

/** Placeholder exibido quando um valor está ausente. */
export const EMPTY_VALUE_PLACEHOLDER = '—';

/**
 * Formata uma data (ISO date-time) para `DD/MM/AAAA`.
 *
 * Usa apenas a parte de data (`YYYY-MM-DD`) para preservar o dia do calendário,
 * evitando o deslocamento de fuso que ocorreria ao converter o horário UTC para
 * o fuso local.
 *
 * @param value - Data em string (date-time) vinda da API, ou nula.
 * @returns Data formatada, ou `—` quando ausente/inválida.
 */
export function formatClassPlanningDate(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE_PLACEHOLDER;
  const parsed = dayjs(value.slice(0, 10));
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : EMPTY_VALUE_PLACEHOLDER;
}

/** Retorna o valor, ou `—` quando ausente/vazio. */
export function formatClassPlanningText(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Formata o intervalo de horário de uma aula (`HH:mm - HH:mm`) a partir do
 * início e do fim (ISO date-time).
 *
 * Extrai `HH:mm` diretamente da string ISO (posições 11–16), em vez de
 * `dayjs(...).format('HH:mm')` — o mesmo motivo de `formatClassPlanningDate`
 * usar `slice` na data: `.format()` converte para o fuso horário local do
 * processo, o que tornaria o horário exibido dependente da máquina/CI.
 *
 * @param dateStart - Início da aula (date-time), ou nulo.
 * @param dateFinish - Fim da aula (date-time), ou nulo.
 * @returns Intervalo formatado, ou string vazia quando algum dos dois estiver ausente/inválido
 * (o chamador decide se omite a linha, em vez de exibir um placeholder de horário isolado).
 */
export function formatClassPlanningTimeRange(
  dateStart: string | null | undefined,
  dateFinish: string | null | undefined,
): string {
  if (!dateStart || !dateFinish) return '';
  if (!dayjs(dateStart).isValid() || !dayjs(dateFinish).isValid()) return '';
  return `${dateStart.slice(11, 16)} - ${dateFinish.slice(11, 16)}`;
}
